import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { interval } from 'rxjs';
import { AppointmentsState } from 'src/app/store/dashboard/appointments/appointments.state';
import { AgentListBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/agent-list-behaviour/agent-list-behaviour.state';
import { DirectoryDataBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/directory-behaviour/directory-data/directory-data-behaviour.state';
import { BrickInstancesSelectors } from 'src/app/store/dashboard/brick-wall/brick-instances/brick-instances.selectors';
import { AgentListEntryRemoteDto } from 'src/generated/signalR/agent-list-entry-remote-dto';
import { ContactStatusService, DirectoryEntryRemoteDto, PresenceTypeRemoteDto } from 'src/generated/swagger';
import { UpdateRegistrationOfContactStates } from './contact-states.actions';
import { LineStatusState } from './line-status/line-status.state';
import { MobileStatusState } from './mobile-status/mobile-status.state';
import { PresenceStatusState } from './presence-status/presence-status.state';

// tslint:disable-next-line:no-empty-interface
export interface ContactStatesStateModel {
   // empty interface
}

@State<ContactStatesStateModel>({
   name: 'contactStates',
   defaults: {},
   children: [PresenceStatusState, MobileStatusState, LineStatusState, AppointmentsState],
})
@Injectable()
export class ContactStatesState {
   constructor(private store: Store, private contactStatusService: ContactStatusService) {
      interval(10000).subscribe(() => store.dispatch(new UpdateRegistrationOfContactStates()));
   }

   @Selector([
      BrickInstancesSelectors.allBrickInstances,
      DirectoryDataBehaviourState.getDirectoryEntries,
      AgentListBehaviourState.getFilteredAndSortedAgentList,
   ])
   public static getRequiredContactStatusIds(
      brickInstanceIds: string[],
      getDirectoryEntries: (brickInstanceId: string) => DirectoryEntryRemoteDto[],
      getAgentListFilteredForBrickFn: (brickInstanceId: string) => AgentListEntryRemoteDto[]
   ) {
      const allDirectoryEntries: DirectoryEntryRemoteDto[] = _(brickInstanceIds)
         .flatMap((brickInstanceId) => getDirectoryEntries(brickInstanceId))
         .value();

      const lineStatesIds: string[] = _(allDirectoryEntries)
         .filter((x) => !_.isNil(x.tr87Number) || !_.isNil(x.orgnum))
         .map((x) => [x.tr87Number, x.orgnum])
         .flatMap()
         .value();

      const subscriberIds: number[] = _(allDirectoryEntries)
         .filter((x) => !_.isNil(x.id))
         .map((x) => x.id)
         .flatMap()
         .value();

      const presenceDataFromDirectory = allDirectoryEntries.map((entry) => {
         return {
            ocsId: entry.ocsId,
            presenceType: entry.presenceType?.value,
         };
      });
      const presenceDataFromAgentList = _(brickInstanceIds)
         .flatMap((brickInstanceId) => getAgentListFilteredForBrickFn(brickInstanceId))
         .filter((directoryEntryPresence) => !_.isNil(directoryEntryPresence))
         .map((agentListEntry) => {
            return {
               ocsId: agentListEntry.directoryEntryPresence.presenceId,
               presenceType: agentListEntry.directoryEntryPresence.presenceType,
            };
         })
         .value();

      // Until we want to show other information (appointments, ...) in the agent list also,
      // it is sufficient to take 'presenceDataFromAgentList' into account for presence ids only.
      const presenceStatesIds = _.union(presenceDataFromDirectory, presenceDataFromAgentList).filter((x) => !_.isNil(x.ocsId));

      const mobileStatesIds = _(allDirectoryEntries)
         .filter((x) => !_.isNil(x.fldMobile))
         .map((x) => x.fldMobile)
         .value();

      return {
         lineStatesIds,
         presenceStatesIds,
         mobileStatesIds,
         subscriberIds,
      };
   }

   @Action(UpdateRegistrationOfContactStates)
   public updateRegistrationOfContactStates(ctx: StateContext<ContactStatesStateModel>, action: UpdateRegistrationOfContactStates) {
      const requiredContactStatusIds = this.store.selectSnapshot(ContactStatesState.getRequiredContactStatusIds);
      if (
         _.isEmpty(requiredContactStatusIds.lineStatesIds) &&
         _.isEmpty(requiredContactStatusIds.mobileStatesIds) &&
         _.isEmpty(requiredContactStatusIds.presenceStatesIds) &&
         _.isEmpty(requiredContactStatusIds.subscriberIds)
      ) {
         return;
      }

      // send current registration to server
      return this.contactStatusService.updateRegisterContactStates({
         registerForExtensionNumbers: requiredContactStatusIds.lineStatesIds.filter((x) => !_.isEmpty(x)),
         registerForMobileNumber: requiredContactStatusIds.mobileStatesIds.filter((x) => !_.isEmpty(x)),
         registerForPresenceTypeTuples: requiredContactStatusIds.presenceStatesIds
            .filter((x) => !_.isEmpty(x.ocsId))
            .map(
               (x) =>
                  <PresenceTypeRemoteDto>{
                     ocsId: x.ocsId,
                     presenceType: x.presenceType,
                  }
            ),
         registerForSubscriberIds: requiredContactStatusIds.subscriberIds,
      });
   }
}
